import * as React from "react";
import {useIntl} from "react-intl";
import Table from "./presentational/table/Table";
import TableHeader, {TextAlignment} from "./presentational/table/TableHeader";
import TableData from "./presentational/table/TableData";
import {PROVISIONAL_HATCH_STYLE} from "./presentational/chart/provisional";
import {monthToDate} from "../helper/dateFormat";
import {formatWholeAmount} from "../helper/revenueFormat";

export type MonthColumn = {
    month: string // YYYY-MM
    status: string // CLOSED, PROVISIONAL or FORECAST for the result, BANK, BANK_AND_FORECAST or FORECAST for the liquidity
};

/** Months which are completely known are final, all others are estimated and hatched */
const isFinal = (status: string) => status === 'CLOSED' || status === 'BANK';

export const monthLabel = (intl: ReturnType<typeof useIntl>, month: string) =>
    intl.formatDate(monthToDate(month), {month: 'short', year: '2-digit', timeZone: 'UTC'});

const statusBadge = (intl: ReturnType<typeof useIntl>, status: string) => {
    const styles: Record<string, string> = {
        CLOSED: 'bg-green-100 text-green-800',
        PROVISIONAL: 'bg-yellow-100 text-yellow-800 italic',
        FORECAST: 'bg-gray-100 text-gray-600 italic',
        BANK: 'bg-green-100 text-green-800',
        BANK_AND_FORECAST: 'bg-yellow-100 text-yellow-800 italic',
    };
    const title = ['CLOSED', 'PROVISIONAL', 'FORECAST'].includes(status) ? intl.formatMessage({id: `view.analysisForecastOverview.status.${status}.title`}) : undefined;
    return <span title={title} className={`inline-flex rounded-full px-2 text-xs font-medium leading-5 ${styles[status] ?? styles.FORECAST}`}>{intl.formatMessage({id: `view.analysisForecastOverview.status.${status}`})}</span>;
};

const rowLabelClass = (bold: boolean) =>
    `sticky left-0 z-10 whitespace-nowrap bg-white py-2 pl-4 pr-3 text-left text-sm sm:pl-6 ${bold ? 'font-semibold text-gray-900 border-t border-gray-300' : 'text-gray-700'}`;

const amountClass = (amount: number, isClosed: boolean, bold: boolean) =>
    `${amount < 0 ? 'text-red-600' : 'text-gray-900'} ${isClosed ? '' : 'italic'} ${bold ? 'font-semibold' : ''}`;

type MonthTableProps = {
    months: readonly MonthColumn[]
    children: React.ReactNode // the rows
};

/**
 * A table with one column per month of the forecast and the status of each month in its header.
 * The result and the liquidity have different statuses.
 * The label column stays in place when the table is scrolled horizontally.
 */
export const MonthTable: React.FC<MonthTableProps> = ({months, children}) => {
    const intl = useIntl();
    return (
        <div className="overflow-x-auto">
            <Table>
                <thead>
                    <tr>
                        <th className="sticky left-0 z-10 bg-white py-2 pl-4 pr-3 sm:pl-6"/>
                        {months.map(month => (
                            <TableHeader key={month.month} textAlignment={TextAlignment.Right} className={`px-3 normal-case text-gray-700 ${isFinal(month.status) ? '' : 'italic'}`}>
                                <div>{monthLabel(intl, month.month)}</div>
                                <div className="mt-1 font-normal">{statusBadge(intl, month.status)}</div>
                            </TableHeader>
                        ))}
                    </tr>
                </thead>
                <tbody>{children}</tbody>
            </Table>
        </div>
    );
};

type MonthCellProps = {
    month: MonthColumn
    amount: number
    bold?: boolean
    isSelected?: boolean
    children?: React.ReactNode // replaces the formatted amount
};

/**
 * The cell of a month: red if negative, italic and hatched unless the month is final.
 */
export const MonthCell: React.FC<MonthCellProps> = ({month, amount, bold = false, isSelected = false, children}) => {
    const isClosed = isFinal(month.status);
    return (
        <TableData
            textAlignment={TextAlignment.Right}
            className={`px-3 tabular-nums ${bold ? 'border-t border-gray-300' : ''} ${isSelected ? 'ring-2 ring-inset ring-blue-400' : ''}`}
            style={isClosed ? undefined : PROVISIONAL_HATCH_STYLE}
        >
            <span className={amountClass(amount, isClosed, bold)}>{children ?? formatWholeAmount(amount)}</span>
        </TableData>
    );
};

type MonthRowProps<M extends MonthColumn> = {
    label: React.ReactNode
    months: readonly M[]
    amountOf: (month: M) => number
    bold?: boolean
    muted?: boolean
};

/**
 * A row of plain amounts, such as a subtotal.
 */
export function MonthAmountRow<M extends MonthColumn>({label, months, amountOf, bold = false, muted = false}: MonthRowProps<M>) {
    return (
        <tr className={bold ? 'border-t border-gray-300' : ''}>
            <th scope="row" className={rowLabelClass(bold) + (muted ? ' text-gray-500 font-normal' : '')}>{label}</th>
            {months.map(month => <MonthCell key={month.month} month={month} amount={amountOf(month)} bold={bold}/>)}
        </tr>
    );
}

type MonthLineRowProps<M extends MonthColumn> = {
    label: string
    months: readonly M[]
    lineOf: (month: M) => { amount: number, parts: readonly unknown[] } | undefined // undefined leaves the cell empty
    selectedMonthIndex: number | null // the month whose cell of this row is selected
    onSelect: (monthIndex: number) => void
    muted?: boolean // for rows which are no part of the sums
};

/**
 * A row whose amounts open the parts they consist of.
 */
export function MonthLineRow<M extends MonthColumn>({label, months, lineOf, selectedMonthIndex, onSelect, muted = false}: MonthLineRowProps<M>) {
    return (
        <tr>
            <th scope="row" className={rowLabelClass(false) + (muted ? ' text-gray-500 italic' : '')}>{label}</th>
            {months.map((month, monthIndex) => {
                const line = lineOf(month);
                if (line === undefined) {
                    return <TableData key={month.month} className="px-3"/>;
                }
                const amount = line.amount;
                return (
                    <MonthCell key={month.month} month={month} amount={amount} isSelected={selectedMonthIndex === monthIndex}>
                        {line.parts.length > 0
                            ? <button type="button" className="tabular-nums hover:underline" onClick={() => onSelect(monthIndex)}>{formatWholeAmount(amount)}</button>
                            : formatWholeAmount(amount)}
                    </MonthCell>
                );
            })}
        </tr>
    );
}

type MonthLabelRowProps<M extends MonthColumn> = {
    label: React.ReactNode
    months: readonly M[]
    children: (month: M) => React.ReactNode
};

/**
 * A row with a label and free content per month, for values that are not a single amount.
 */
export function MonthContentRow<M extends MonthColumn>({label, months, children}: MonthLabelRowProps<M>) {
    return (
        <tr>
            <th scope="row" className={rowLabelClass(false)}>{label}</th>
            {months.map(month => <React.Fragment key={month.month}>{children(month)}</React.Fragment>)}
        </tr>
    );
}
